import { QueryFilter } from "../queries/queries.models";
import { UnitOfWorkItem } from "../../utilities/unit-of-work-observer";
import { RulesHolder } from "../../client-validation-system/validations.models";
import { WorkflowConfigurationModel } from "../workflow/workflow.models";
import { PropertyMetaModel } from "../basics/basics.models";
import { BaseValidationResponse } from "../validations/validations.models";
import { IEntityTypeIdentifier } from "@rightship-tech/common-ui/models/basic.models";

export interface OnboardingConfigModel extends IEntityTypeIdentifier {
    onboardingConfigId: number;
    name: string;
    guid: string;
    isActive: boolean;
    isSelectable: boolean;
    basicMetaConfig: string;
    advancedMetaConfig: string;
    offeringsMetaConfig: string;
    pluginMetaConfig: string;
    isBasicMetaFormStyle: boolean;
    isAdvancedMetaFormStyle: boolean;
    offerings: OfferingModel[];
    onboardingPlugin?: OnboardingPluginModel;
    offeringsSelectionConfigs: OfferingsSelectionConfigModel[];
    landingPages: OnboardingConfigLandingPageModel[];
    sections: string[];
    allowedCountries: OnboardingRequestAllowedCountry[];
    onboardingRequiredDocumentTypes: OnboardingRequiredDocumentTypeModel[];
    onboardingConfigVerifications: OnboardingConfigVerificationModel[];
    workflowConfigurationId: number;
    workflowConfiguration: WorkflowConfigurationModel;
    onboardingPluginId: number;
    termsAndConditions: string;
    isGeoLocationEnabled: boolean;
    navigationHandler: string;
    doNotUseFallback: boolean;
    isGdprConsentEnabled: boolean;
    onboardingWhatsNeededSteps: OnboardingWhatsNeededStepModel[];
    rulesScript: string;
    rulesScriptLanguage: string;
    onboardingCategoryId?: number;
    onboardingPluginEntityTypeId?: number;
    onboardingVersionCategoryId?: number;
}

export interface OnboardingWhatsNeededStepModel {
    id: number;
    onboardingWhatsNeededStepId: number;
    icon: string;
    title: string;
    description: string;
    onboardingConfigId: number;
    isForExistingCustomer: boolean;
}

export interface OnboardingRequestAllowedCountry {
    countryId: number;
    onboardingConfigId: number;
}

export interface OnboardingLandingPage {
    name: string;
    completeOnboardingPages: string[];
}

export interface OnboardingRequiredDocumentTypeModel {
    onboardingRequiredDocumentTypeId?: number;
    onboardingConfigId?: number;
    attachmentTypeId: number;
    name?: string;
    templateFilePath?: string;
    guid?: string;
    query?: string;
    isMandatory?: boolean;
}

export interface OnboardingPluginModel {
    onboardingPluginId: number;
    title: string;
    internalName: string;
    isActive: boolean;
    isSelectable: boolean;
    isDynamic: boolean;
}

export interface OfferingModel extends IEntityTypeIdentifier {
    offeringId: number;
    onboardingConfigId: number;
    onboardingConfig?: OnboardingConfigModel;
    name: string;
    description: string;
    imageUrl: string;
    videoUrl: string;
    additionalFileUrl: string;
    metaConfig: string;
    guid: string;
    isActive: boolean;
    isSelectable: boolean;
    rank: number;
    showAlways: boolean;
    staticMeta: string;
    staticMetaJson: any;
    isMetaFormStyle: boolean;
}

export interface OnboardingRequestDocumentModel {
    onboardingRequestDocumentId?: number;
    onboardingRequestId?: number;
    onboardingRequestGuid?: string;
    documentName?: string;
    documentPath?: string;
    attachmentTypeId: number;
    groupingGuid?: string;
}

export interface OnboardingConfigLandingPageModel extends IEntityTypeIdentifier {
    onboardingConfigLandingPageId: number;
    onboardingConfigId: number;
    landingPage: string;
    completeRequestLandingPage: string;
    guid: string;
}

export interface OnboardingRequestModel {
    onboardingRequestId: number;
    landingPage: number;
    onboardingConfig: OnboardingConfigModel;
    offeringsMeta: string;
    onboardingRequestGuid?: string;
    offeringsSelected: OfferingSelectedInOnboardingRequestModel[];
    uploadedDocuments: OnboardingRequestDocumentModel[];
    verifications: OnboardingRequestVerificationModel[];
    urlsHistoryJson: string;
    modelMeta: string;
    clientNotes: string;
    isCompleted: boolean;
    rules: OnboardingRequestRuleModel[];
    rejectionReason?: string;
    isRejected?: boolean;
}

export interface OnboardingRequestRuleModel {
    onboardingRequestRuleId: number;
    property: string;
    message: string;
    onboardingRequestId: number;
    clientSideParameters: [];
}

export interface OnboardingRequestVerificationModel {
    onboardingRequestVerificationId: number;
    onboardingRequestId: number;
    verificationDate: string;
    onboardingVerificationPluginId: number;
    onboardingVerificationPlugin: OnboardingVerificationPluginModel;
    meta: string;
}

export interface OfferingSelectedInOnboardingRequestModel {
    offeringSelectedInOnboardingRequestId: number;
    onboardingRequestId: number;
    offeringId: number;
    offering: OfferingModel;
}

export interface OnboardingConfigVerificationModel {
    onboardingConfigVerificationId: number;
    onboardingVerificationPluginId: number;
    onboardingVerificationPlugin?: OnboardingVerificationPluginModel;
    onboardingConfigId: number;
    meta: string;
    metaInstance?: any;
    sequenceNumber: number;
    retries: number;
    whatsNeededIcon: string;
    whatsNeededTitle: string;
    whatsNeededDescription: string;
}

export interface OnboardingVerificationPluginModel {
    onboardingVerificationPluginId: number;
    title: string;
    internalName: string;
    guid: string;
    isActive: boolean;
    isSelectable: boolean;
    sequenceNumber: number;
    isDefault: boolean;
    tags: string;
}

export interface OfferingsSelectionConfigModel extends IEntityTypeIdentifier {
    offeringsSelectionConfigId: number;
    queries: QueryFilter[];
    configName: string;
    isSharedGlobally: boolean;
    isActive: boolean;
    dateCreated: string | Date;
    createdById: number;
    queryBody: string;
    onboardingConfigId: number;
    offeringsSelected: OfferingSelectedInConfigModel[];
    guid: string;
}

export interface OfferingSelectedInConfigModel {
    offeringSelectedInConfigId: number;
    offeringsSelectionConfigId: number;
    offeringId: number;
    offering?: OfferingModel;
}

export interface OnboardingModelMeta {
    entityIdentifier: string;
    properties: PropertyMetaModel[];
}

export interface FormFileWithType {
    file?: File;
    documentName?: string;
    documentPath?: string;
    attachmentTypeId: number;
    groupingGuid?: string;
}

export interface OnboardingRequestEntityConfigModel {
    uri: string;
    typeSingular: string;
    title: string;
    entityTypeId: number;
    oDataModel: string;
}

export interface OfferingMetaBagModel {
    offeringId: number;
    bag: any;
}

export interface SaveOnboardingConfigRequest {
    id: number;
    changes: { [index: string]: UnitOfWorkItem };
}

export interface RunOnboardingRulesTestRequest {
    onboardingConfigId: number;
    properties: OnboardingRulesModelProperty[];
}

export interface OnboardingRulesModelProperty {
    propertyDisplayName: string;
    propertyName: string;
    value: string;
}

export interface SaveOfferingRequest {
    id: number;
    changes: { [index: string]: UnitOfWorkItem };
    imageFile: File;
    videoUrlHasChanged: boolean;
    videoUrl: string;
    documentFile: File;
    imageHasChanged: boolean;
    documentHasChanged: boolean;
}

export interface CreateDynamicOnboardingPluginRequest {
    displayName: string;
    displayODataName: string;
}

export interface ValidateOnboardingRequest extends BaseOnboardingRequest {
    meta: { [prop: string]: string; };
    isBasicMeta: boolean;
    pageNumber?: number;
    guid: string;
}

export interface ValidateOnboardingOfferingRequest extends BaseOnboardingRequest {
    offeringMeta: OfferingMetaBagModel;
    pageNumber: number;
}

export interface ValidateOnboardingOfferingsRequest extends BaseOnboardingRequest {
    offeringsMeta: OfferingMetaBagModel[];
}

export interface CreateOnboardingRequest extends BaseOnboardingRequest {
    meta: { [prop: string]: string; };
    guid: string;
}

export interface CloneOnboardingRequest extends BaseOnboardingRequest {
    onboardingId: number;
    onboardingName: string;
    internalName: string;
}

export interface CloneOnboardingPluginRequest extends BaseOnboardingRequest {
    onboardingPluginId: number;
    displayName: string;
    displayODataName: string;
}

export interface UpdateAdvancedMetaRequest {
    meta: any;
    offeringsMeta: OfferingMetaBagModel[];
}

export interface UploadDocumentsRequest extends BaseOnboardingRequest {
    documents: FormFileWithType[];
    documentsToRemove: number[];
    modelMeta: any;
}

export interface GetRequiredDocumentTypesRequest {
    onboardingRequestGuid?: string;
    guid?: string;
}

export interface BaseOnboardingRequest {
    onboardingRequestGuid?: string;
    guid?: string;
}

export interface OfferingsSelectionRequest extends BaseOnboardingRequest {
    offerings: (number | string)[];
}


export interface CreateOnboardingRequestResponse extends OnboardingModelValidationResponse {
    onboardingRequestGuid: string;
    modelMeta: any;
}

export interface CloneOnboardingResponse {
    isSuccess: boolean;
}

export interface RunOnboardingRulesTestResponse {
    rules: OnboardingRequestRuleModel[];
    error: string;
}

export interface CloneOnboardingPluginResponse extends CreateDynamicOnboardingPluginResponse {
    isSuccess: boolean;
}

export interface OfferingsForOnboardingRequestResponse {
    offerings: OfferingModel[];
    otherOfferings: OfferingModel[];
    offeringsSelected: OfferingModel[];
}

export interface GetVerificationsResponse {
    verifications: OnboardingConfigVerificationModel[];
}

export interface GetAdvancedMetaResponse {
    offeringsSelected: OfferingModel[];
    offeringModelValidations: RulesHolder;
    offeringModelProperties: PropertyMetaModel[];
    onboardingModelProperties: PropertyMetaModel[];
    modelMeta: string;
}

export interface GetRequiredDocumentTypesResponse {
    requiredDocumentTypes: OnboardingRequiredDocumentTypeModel[];
}

export interface UpdateAdvancedMetaResponse extends OnboardingModelValidationResponse {
    onboardingRequestGuid: string;
    modelMeta: any;
}

export interface OfferingsSelectionResponse {
    offerings: number[];
}

export interface SaveOfferingResponse extends BaseValidationResponse {
    offering: OfferingModel;
    id: number;
}

export interface CreateDynamicOnboardingPluginResponse {
    existingPluginWithSameDisplayName: boolean;
    existingPluginWithSameODataDisplayName: boolean;
    existingEntityWithSameName: boolean;
}

export interface GetOnboardingClientConfigResponse {
    isValidCountry: boolean;
    country: string;
    pluginName: string;
    basicMetaConfig: string;
    pluginMetaConfig: string;
    advancedMetaConfig: string;
    offeringsMetaConfig: string;
    isBasicMetaFormStyle: boolean;
    isAdvancedMetaFormStyle: boolean;
    termsAndConditions: string;
    landingPage: string;
    onboardingRequestsPath: string;
    completeRequestLandingPage: string;
    onboardingModelValidations: RulesHolder;
    offeringsCount: number;
    offeringsWithVisibleMetaCount: number;
    onboardingRequiredDocumentTypesCount: number;
    onboardingModelProperties: PropertyMetaModel[];
    offeringModelProperties: PropertyMetaModel[];
    navigationHandler: string;
    doNotUseFallback: boolean;
    onboardingName: string;
    isGdprConsentEnabled: boolean;
    whatsNeededSteps: OnboardingWhatsNeededStepModel[];
    entityTypeId: number;
}

export interface UploadDocumentsResponse {
    uploadedDocuments: OnboardingRequestDocumentModel[];
    removedDocuments: OnboardingRequestDocumentModel[];
    onboardingRequestGuid: string;
}

export interface CompleteOnboardingResponse {
    isCompleted: boolean;
    onboardingRequest: OnboardingRequestModel;
    validationItems: OnboardingModelValidationItem[];
}

export interface ValidateOnboardingOfferingsResponse {
    validatedOnboardingOfferings: ValidatedOnboardingOfferingResponse[];
}

export interface ValidatedOnboardingOfferingResponse {
    offeringId: number;
    validationItems: OnboardingModelValidationItem[];
}

export interface OnboardingModelValidationItem {
    propertyName: string;
    message: string;
    meta: string;
}

export interface SaveVerificationMetaResponse {
    saved: boolean;
}

export interface GetOnboardingConfigResponse {
    onboardingConfig: OnboardingConfigModel;
    modelMeta: OnboardingModelMeta;
    offeringMeta: OnboardingModelMeta;
    landingPages: OnboardingLandingPage[];
    sections: string[];
    verificationPlugins: OnboardingVerificationPluginModel[];
    offeringConfigMetaPath: string;
    pluginConfigMetaPath: string;
}

export interface OnboardingRequestEntityConfigsResponse {
    entityConfigs: OnboardingRequestEntityConfigModel[];
}

export interface GetOnboardingRequestResponse {
    onboardingRequest: OnboardingRequestModel;
    entityTypeId: number;
    onboardingModelProperties: PropertyMetaModel[];
    offeringModelProperties: PropertyMetaModel[];
    onboardingCustomContentPath: string;
    onboardingHeaderPath: string;
    onboardingAfterBasicMetaPath: string;
    onboardingRequestsPath: string;
    pluginName: string;
}

export interface UploadFilesResponse {
    files: FileUploaded[];
}

export interface FileUploaded {
    name: string;
    url: string;
}

export enum OnboardingRequestStepEnum {
    BasicInfo = 1,
    OfferingsSelection = 2,
    AdvancedInfo = 3,
    Identification = 4,
    Verification = 5,
    Completed = 6
}

export interface OnboardingModelValidationResponse {
    validationItems: OnboardingModelValidationItem[];
}

export interface OnboardingModelValidationItem {
    propertyName: string;
    camelCasePropertyName: string;
    message: string;
    meta: string;
}

export interface OfferingMetaConfigItem {
    title: string;
    icon: string;
    descriptionHtml: string;
}

export interface OfferingsMetaConfigModel {
    title?: string;
    subtitle?: string;
    items?: OfferingMetaConfigItem[];
}

export interface DeleteOnboardingConfigRequest {
    onboardingConfigId: number;
}

export interface DeleteOnboardingPluginRequest {
    onboardingPluginId: number;
}

export interface DeleteOnboardingConfigResponse {
    isSuccess: boolean;
}

export interface DeleteOnboardingPluginResponse {
    isSuccess: boolean;
}

